import { type Instance, types } from 'mobx-state-tree';

import {
  AnalyticsAdRatingStatus,
  JobAdType,
  JobStatus,
  PostChannel,
  WorkType,
} from 'src/App/AppPageBlock/graphql/hirer-types.generated';

import { DEFAULT_ID } from '../../constants/job';

import { isValidPostChannel } from './utils/isValidPostChannel';

/**
 * Enums copied from hirer graph as the `payType` field is a `String`.
 * See https://github.com/SEEK-Jobs/hirer-graphql-api/blob/e4d0967c89165a4fb2731ff1211bd80f0c7a38d2/src/schema/job/type.ts#L34-L39
 */
export enum SalaryType {
  ANNUAL_PACKAGE = 'ANNUAL_PACKAGE',
  ANNUAL_COMMISSION = 'ANNUAL_COMMISSION',
  COMMISSION_ONLY = 'COMMISSION_ONLY',
  HOURLY_RATE = 'HOURLY_RATE',
  MONTHLY = 'MONTHLY',
}

const SalaryTypes: SalaryType[] = Object.values(SalaryType);

interface JobLocation {
  id: string;
  description: string;
  country: string;
  type: string;
}

const JobInformation = types
  .model('JobInformation', {
    title: types.optional(types.string, ''),
    positionId: types.optional(types.number, DEFAULT_ID),
    postedBy: types.optional(types.string, ''),
    adType: types.maybeNull(types.enumeration(Object.values(JobAdType))),
    billableApplicants: types.union(
      types.model({
        success: types.literal(true),
        billableApplicantsCount: types.number,
        maxBillableApplicants: types.number,
      }),
      types.model({
        success: types.literal(false),
      }),
      types.undefined,
    ),
    productDisplayName: types.maybeNull(types.string),
    workType: types.maybeNull(types.enumeration(Object.values(WorkType))),
    salary: types.maybeNull(types.string),
    salaryMin: types.maybeNull(types.number), // Minumum salary in lowest denomination.
    salaryMax: types.maybeNull(types.number), // Maximum salary in lowest denomination.
    salaryCurrency: types.maybeNull(types.string),
    salaryType: types.maybeNull(types.enumeration(Object.values(SalaryType))),
    locations: types.optional(types.array(types.string), []),
    locationIds: types.optional(types.array(types.string), []),
    jobCountry: types.optional(types.string, ''),
    classification: types.maybeNull(types.string),
    categoryId: types.maybeNull(types.string),
    jobCreatedDate: types.optional(types.string, ''),
    subClassification: types.maybeNull(types.string),
    positionCreatedDate: types.optional(types.string, ''),
    listingStartedDate: types.optional(types.string, ''),
    expiredDate: types.maybeNull(types.string),
    internalReference: types.maybe(types.string),
    listingId: types.optional(types.number, DEFAULT_ID),
    status: types.optional(
      types.enumeration(Object.values(JobStatus)),
      JobStatus.ACTIVE,
    ),
    questionnaireId: types.maybeNull(types.string),
    jobId: types.maybeNull(types.number),
    postChannel: types.optional(
      types.enumeration(Object.values(PostChannel)),
      PostChannel.DIRECT,
    ),
    numberOfDaysRemaining: types.maybeNull(types.number),
    analyticsAdRatingStatus: types.maybeNull(
      types.enumeration(Object.values(AnalyticsAdRatingStatus)),
    ),
  })
  .views((self) => ({
    get hasPerformanceStats() {
      switch (self.analyticsAdRatingStatus) {
        case AnalyticsAdRatingStatus.WITHHELD:
        case AnalyticsAdRatingStatus.AVAILABLE_PENDING_RATING:
        case AnalyticsAdRatingStatus.AVAILABLE_CANNOT_RATE:
        case AnalyticsAdRatingStatus.AVAILABLE_WITH_RATING:
          return true;
      }
      return false;
    },
    get hasSalary() {
      return Boolean(self.salaryMin) || self.salaryMin === 0;
    },
    get salaryIsHidden() {
      return !(this.hasSalary && Boolean(self.salary));
    },
  }))
  .actions((self) => {
    const setSalaryType = (payType: string | null): void => {
      self.salaryType = SalaryTypes.includes((payType ?? '') as SalaryType)
        ? (payType as SalaryType)
        : null;
    };

    const setPostChannel = (postChannel: string): void => {
      self.postChannel = isValidPostChannel(postChannel)
        ? postChannel
        : PostChannel.DIRECT;
    };

    const setJobCountry = (locations: JobLocation[]): void => {
      self.jobCountry = locations[0].country.toUpperCase();
    };

    return { setPostChannel, setSalaryType, setJobCountry };
  });

export interface IJobInformation extends Instance<typeof JobInformation> {}
export default JobInformation;
